import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NavBar from '../components/NavBar/Navigation';
import Footer from '../components/Footer/Footer';
import QMSBannerImage from '../assets/qms.png';
import './Qms.css';

const COL = {
  version: 'text_mkv1gaj3',
  docRef: 'text_mkv1p89m',
  pdfFile: 'file_mkv1cspv',
  sharepointLink: 'link_mkwk26tr',
  classification: 'color_mkv15vr4',
  docType: 'color_mm52mwa6',
  owner: 'multiple_person_mm60ge8y',
};

const MONDAY_BOARD_ID = process.env.REACT_APP_MONDAY_QMS_BOARD_ID || '5099960394';
const MONDAY_TOKEN = process.env.REACT_APP_MONDAY_TOKEN;
const MONDAY_API_URL = 'https://api.monday.com/v2';

const normalizeText = (value = '') =>
  String(value).replace(/\s+/g, ' ').trim();

/* Loose key: lowercase, no punctuation — "CEO's Office" === "CEO OFFICE" */
const deptKey = (value = '') =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9 ]/g, '')
    .trim();

/* ── Department aliases ─────────────────────────────────────
   Board group titles that should display under a different
   name. The Monday group is "Office of the CEO"; on the
   intranet it is shown (and filtered) as "CEO Office".
   Nothing is hidden any more, so its documents appear.
   ────────────────────────────────────────────────────────── */
const CEO_DEPT = 'CEO Office';

/* Any spelling of the CEO department collapses into ONE name:
   "Office of the CEO", "CEO Office", "CEO's Office", "Office of CEO"… */
const isCeoDept = (value = '') => {
  const key = deptKey(value);
  return /\bceo\b/.test(key) || key === 'ceos office';
};

const canonicalDept = (value = '') => {
  const clean = normalizeText(value);
  return isCeoDept(clean) ? CEO_DEPT : clean;
};

/* Departments listed here always come first, in this order */
const DEPT_PRIORITY = ['ceo office', 'operations'];

/* Shown as a filter even when the group has no documents yet */
const ALWAYS_SHOW_DEPTS = [CEO_DEPT];

const priorityIndex = (value) => {
  const idx = DEPT_PRIORITY.indexOf(deptKey(value));
  return idx === -1 ? DEPT_PRIORITY.length : idx;
};

const mondayFetch = async (query) => {
  const res = await fetch(MONDAY_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: MONDAY_TOKEN,
      'API-Version': '2024-01',
    },
    body: JSON.stringify({ query }),
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(json?.errors?.[0]?.message || 'Monday request failed');
  }

  return json;
};

const buildQuery = (boardId) => `
  query {
    boards(ids: ${boardId}) {
      groups {
        id
        title
      }
      columns(ids: ["${COL.docType}"]) {
        id
        settings_str
      }
      items_page(limit: 500) {
        items {
          id
          name
          group {
            id
            title
          }
          assets(column_ids: ["${COL.pdfFile}"]) {
            id
            name
            file_extension
          }
          column_values(ids: [
            "${COL.version}",
            "${COL.docRef}",
            "${COL.pdfFile}",
            "${COL.sharepointLink}",
            "${COL.classification}",
            "${COL.docType}",
            "${COL.owner}"
          ]) {
            id
            type
            text
            value
            ... on StatusValue {
              label
            }
          }
        }
      }
    }
  }
`;

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/* ── Status column labels ─────────────────────────────────────
   Reads every label defined on a status column (in board order),
   so a newly added label such as "Memo" shows up as a filter even
   before any document uses it.
   ────────────────────────────────────────────────────────── */
const getStatusLabels = (settingsStr) => {
  const settings = safeJsonParse(settingsStr);
  if (!settings?.labels) return [];

  const deactivated = new Set(
    (settings.deactivated_labels || []).map((id) => String(id))
  );
  const positions = settings.labels_positions_v2 || {};

  return Object.entries(settings.labels)
    .filter(
      ([id, label]) => !deactivated.has(String(id)) && normalizeText(label)
    )
    .sort(
      ([a], [b]) => (positions[a] ?? Number(a)) - (positions[b] ?? Number(b))
    )
    .map(([, label]) => normalizeText(label));
};

const getColObject = (columnValues, id) =>
  columnValues.find((c) => c.id === id);

const getTextCol = (columnValues, id) => {
  const col = getColObject(columnValues, id);
  if (!col) return '';

  if (typeof col.label === 'string' && normalizeText(col.label)) {
    return normalizeText(col.label);
  }

  if (typeof col.text === 'string' && normalizeText(col.text)) {
    return normalizeText(col.text);
  }

  return '';
};

const getLinkCol = (columnValues, id) => {
  const col = getColObject(columnValues, id);
  if (!col) return null;

  if (typeof col.value === 'string' && col.value) {
    const parsed = safeJsonParse(col.value);
    if (parsed?.url) {
      return {
        url: parsed.url,
        label: normalizeText(parsed.text) || normalizeText(col.text) || parsed.url,
      };
    }
  }

  if (typeof col.text === 'string' && normalizeText(col.text)) {
    return { url: normalizeText(col.text), label: normalizeText(col.text) };
  }

  return null;
};

/* ── File type helpers ────────────────────────────────────── */
const extensionFrom = (input = '') => {
  const raw = String(input).split('?')[0].split('#')[0].trim();
  const last = raw.split('/').pop() || raw;
  const parts = last.split('.');
  if (parts.length < 2) return '';
  const ext = parts.pop().trim().toUpperCase();
  if (!ext || ext.length > 5 || !/^[A-Z0-9]+$/.test(ext)) return '';
  return ext;
};

const extensionGroup = (ext) => {
  switch (ext) {
    case 'PDF':
      return 'pdf';
    case 'DOC':
    case 'DOCX':
    case 'DOCM':
    case 'ODT':
    case 'RTF':
      return 'word';
    case 'XLS':
    case 'XLSX':
    case 'XLSM':
    case 'CSV':
    case 'ODS':
      return 'excel';
    case 'PPT':
    case 'PPTX':
    case 'ODP':
      return 'ppt';
    case 'PNG':
    case 'JPG':
    case 'JPEG':
    case 'GIF':
    case 'SVG':
    case 'WEBP':
      return 'image';
    case 'ZIP':
    case 'RAR':
    case '7Z':
      return 'archive';
    default:
      return 'other';
  }
};

const getFiles = (item, columnValues, id) => {
  const files = [];

  (item.assets || []).forEach((asset) => {
    const ext =
      extensionFrom(asset.file_extension || '') ||
      extensionFrom(asset.name || '');
    files.push({
      key: asset.id || asset.name,
      name: normalizeText(asset.name) || 'Document',
      ext: ext || 'FILE',
      group: extensionGroup(ext),
    });
  });

  if (files.length) return files;

  const col = getColObject(columnValues, id);
  if (!col) return files;

  const parsed = typeof col.value === 'string' ? safeJsonParse(col.value) : null;
  if (parsed?.files?.length) {
    parsed.files.forEach((f, i) => {
      const source = f.name || f.fileName || f.url || '';
      const ext = extensionFrom(source);
      files.push({
        key: f.assetId || f.objectId || `${i}`,
        name: normalizeText(f.name || f.fileName) || 'Document',
        ext: ext || 'FILE',
        group: extensionGroup(ext),
      });
    });
    return files;
  }

  if (typeof col.text === 'string' && normalizeText(col.text)) {
    col.text
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((entry, i) => {
        const ext = extensionFrom(entry);
        files.push({
          key: `${i}`,
          name: entry.split('/').pop() || 'Document',
          ext: ext || 'FILE',
          group: extensionGroup(ext),
        });
      });
  }

  return files;
};

const sortDocs = (list) =>
  [...list].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );

const Qms = () => {
  const [docs, setDocs] = useState([]);
  const [groupOrder, setGroupOrder] = useState([]);
  const [docTypeLabels, setDocTypeLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeDept, setActiveDept] = useState('All');
  const [activeDocType, setActiveDocType] = useState('All');

  useEffect(() => {
    const fetchRegister = async () => {
      try {
        setLoading(true);
        setError(null);

        const json = await mondayFetch(buildQuery(MONDAY_BOARD_ID));
        if (json.errors) throw new Error(json.errors[0]?.message || 'API error');

        const board = json?.data?.boards?.[0];
        if (!board) throw new Error('Board not found');

        setGroupOrder(board.groups.map((g) => canonicalDept(g.title)));

        const docTypeColumn = (board.columns || []).find(
          (c) => c.id === COL.docType
        );
        setDocTypeLabels(getStatusLabels(docTypeColumn?.settings_str));

        const parsed = board.items_page.items
          .map((item) => {
            const sharepoint = getLinkCol(item.column_values, COL.sharepointLink);
            const files = getFiles(item, item.column_values, COL.pdfFile);

            return {
              id: item.id,
              name: normalizeText(item.name),
              department: canonicalDept(item.group?.title),
              version: getTextCol(item.column_values, COL.version),
              docRef: getTextCol(item.column_values, COL.docRef),
              classification: getTextCol(item.column_values, COL.classification),
              docType: getTextCol(item.column_values, COL.docType),
              owner: getTextCol(item.column_values, COL.owner),
              files,
              sharepoint,
            };
          })
          .filter((d) => d.name && d.name !== '-');

        setDocs(parsed);
      } catch (err) {
        console.error('QMS Register fetch error:', err);
        setError(err.message || 'Failed to load QMS register');
      } finally {
        setLoading(false);
      }
    };

    fetchRegister();
  }, []);

  /* Board order is the tie-breaker once priority is applied */
  const boardIndex = useMemo(() => {
    const map = new Map();
    groupOrder.forEach((title, i) => {
      const key = deptKey(title);
      if (!map.has(key)) map.set(key, i);
    });
    return map;
  }, [groupOrder]);

  const sortDepartments = useMemo(
    () => (list) =>
      [...list].sort((a, b) => {
        const pa = priorityIndex(a);
        const pb = priorityIndex(b);
        if (pa !== pb) return pa - pb;

        const ba = boardIndex.has(deptKey(a)) ? boardIndex.get(deptKey(a)) : 999;
        const bb = boardIndex.has(deptKey(b)) ? boardIndex.get(deptKey(b)) : 999;
        if (ba !== bb) return ba - bb;

        return a.localeCompare(b, undefined, { sensitivity: 'base' });
      }),
    [boardIndex]
  );

  const departments = useMemo(() => {
    const seen = new Map();

    const add = (name) => {
      const clean = canonicalDept(name);
      if (!clean) return;
      if (!seen.has(deptKey(clean))) seen.set(deptKey(clean), clean);
    };

    groupOrder.forEach(add);
    docs.forEach((d) => add(d.department));
    ALWAYS_SHOW_DEPTS.forEach(add);

    return ['All', ...sortDepartments([...seen.values()])];
  }, [docs, groupOrder, sortDepartments]);

  /* Every label defined on the Monday column, plus any type in use */
  const docTypes = useMemo(() => {
    const types = [
      ...new Set([
        ...docTypeLabels,
        ...docs.map((d) => d.docType).filter(Boolean),
      ]),
    ];
    types.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return ['All', ...types];
  }, [docs, docTypeLabels]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();

    return sortDocs(
      docs.filter((d) => {
        const matchesDept =
          activeDept === 'All' || deptKey(d.department) === deptKey(activeDept);
        const matchesType = activeDocType === 'All' || d.docType === activeDocType;
        const matchesSearch =
          !q ||
          d.name.toLowerCase().includes(q) ||
          d.department.toLowerCase().includes(q) ||
          d.docRef.toLowerCase().includes(q);

        return matchesDept && matchesType && matchesSearch;
      })
    );
  }, [docs, search, activeDept, activeDocType]);

  const grouped = useMemo(() => {
    if (activeDept !== 'All') return null;

    const map = new Map();

    filtered.forEach((d) => {
      const key = d.department || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    });

    const ordered = sortDepartments([...map.keys()]);

    return new Map(ordered.map((dept) => [dept, map.get(dept)]));
  }, [filtered, activeDept, sortDepartments]);

  const DocCard = ({ doc, index }) => {
    const openLink = () => {
      if (doc.sharepoint?.url) {
        window.open(doc.sharepoint.url, '_blank', 'noopener,noreferrer');
      }
    };

    const visibleFiles = doc.files.slice(0, 2);
    const extraFiles = doc.files.length - visibleFiles.length;

    return (
      <motion.div
        className="qms-card"
        initial={{ opacity: 0, y: 16, filter: 'blur(4px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        exit={{ opacity: 0, y: 16, filter: 'blur(4px)' }}
        transition={{
          duration: 0.35,
          ease: 'easeOut',
          delay: Math.min(index * 0.04, 0.6),
        }}
      >
        <div className="qms-card-top">
          <p className="qms-card-name">{doc.name}</p>
          {doc.docType && <span className="qms-badge qms-badge--type">{doc.docType}</span>}
        </div>

        <div className="qms-card-meta">
          {doc.docRef && (
            <div className="qms-card-info">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16v16H4z" />
                <path d="M8 9h8M8 13h6" />
              </svg>
              <span>{doc.docRef}</span>
            </div>
          )}

          {doc.version && (
            <div className="qms-card-info">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9" />
                <path d="M3 4v5h5" />
              </svg>
              <span>Version {doc.version}</span>
            </div>
          )}

          {doc.classification && (
            <div className="qms-card-info">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2 3 7v6c0 5 4 9 9 9s9-4 9-9V7z" />
              </svg>
              <span>{doc.classification}</span>
            </div>
          )}

          {doc.owner && (
            <div className="qms-card-info">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span>{doc.owner}</span>
            </div>
          )}
        </div>

        <div className="qms-card-meta qms-card-meta--footer">
          <div className="qms-file-badges">
            {visibleFiles.map((file) => (
              <span
                key={file.key}
                className={`qms-file-badge qms-file-badge--${file.group}`}
                title={file.name}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                </svg>
                {file.ext}
              </span>
            ))}

            {extraFiles > 0 && (
              <span className="qms-file-badge qms-file-badge--more">
                +{extraFiles}
              </span>
            )}
          </div>

          {doc.sharepoint ? (
            <span
              className="qms-card-link"
              role="button"
              tabIndex={0}
              onClick={openLink}
              onKeyDown={(e) => e.key === 'Enter' && openLink()}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.07 0l1.93-1.93a5 5 0 0 0-7.07-7.07L10.5 5.5" />
                <path d="M14 11a5 5 0 0 0-7.07 0l-1.93 1.93a5 5 0 0 0 7.07 7.07L13.5 18.5" />
              </svg>
              Open in SharePoint
            </span>
          ) : (
            <span className="qms-card-no-link">No link</span>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="qms-page">
      <NavBar />

      <div
        className="qms-banner"
        style={{ backgroundImage: `url(${QMSBannerImage})` }}
      >
        <div className="qms-banner-overlay" />
        <div className="qms-banner-text">
          <h2 className="qms-banner-heading">QMS Register</h2>
          <p className="qms-banner-sub">Quality Management System Documents</p>
        </div>
      </div>

      <div className="qms-controls-wrapper">
        <div className="qms-controls">
          <div className="qms-search-wrap">
            <svg className="qms-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>

            <input
              className="qms-search"
              type="text"
              placeholder="Search by name, department or reference number…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {search && (
              <button
                className="qms-search-clear"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <div className="qms-filter-group">
            <span className="qms-filter-label">Department</span>
            <div className="qms-filter-pills">
              {departments.map((dept) => (
                <button
                  key={dept}
                  className={`qms-pill ${activeDept === dept ? 'qms-pill--active' : ''}`}
                  onClick={() => setActiveDept(dept)}
                >
                  {dept}
                </button>
              ))}
            </div>
          </div>

          <div className="qms-filter-group">
            <span className="qms-filter-label">Document Type</span>
            <div className="qms-filter-pills">
              {docTypes.map((type) => (
                <button
                  key={type}
                  className={`qms-pill ${activeDocType === type ? 'qms-pill--active' : ''}`}
                  onClick={() => setActiveDocType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="qms-page-content">
        {loading && (
          <div className="qms-status">
            <div className="qms-spinner" />
            <p>Loading QMS register…</p>
          </div>
        )}

        {error && !loading && (
          <div className="qms-status qms-status--error">
            <p>Couldn't load the register — {error}</p>
            <p className="qms-status-hint">
              Check your API key and board ID in <code>.env</code>, and confirm your column IDs in the <code>COL</code> map at the top of <code>Qms.js</code>.
            </p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="qms-status">
            <p>No documents match your search.</p>
          </div>
        )}

        {!loading && !error && (
          <AnimatePresence mode="wait">
            {grouped && [...grouped.entries()].map(([dept, members]) => (
              <div key={dept} className="qms-dept-section">
                <h3 className="qms-dept-heading">
                  <span>{dept}</span>
                  <span className="qms-dept-count">{members.length}</span>
                </h3>

                <div className="qms-grid">
                  {members.map((doc, i) => (
                    <DocCard key={doc.id} doc={doc} index={i} />
                  ))}
                </div>
              </div>
            ))}

            {!grouped && (
              <div className="qms-grid">
                {filtered.map((doc, i) => (
                  <DocCard key={doc.id} doc={doc} index={i} />
                ))}
              </div>
            )}
          </AnimatePresence>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Qms;